import type { LivePersonaEditorEvent as LivePersonaEditorInternalEvent } from '@1js/lpe-hostapp-loader-lite';
import type { PersonSourceEvent as PersonSourcesInternalEvent } from '@1js/person-sources-hostapp-loader-lite';
import { AppBarEventTypes } from '@1js/office-start-appbar-events';
import type { CurrentAppPermissionsRequestDialogEventData } from '@1js/office-start-device-permission-types';
import type { LinkedEntity, NovaEvent } from '@nova/types';
import { MeControlEventTypes } from '@mecontrol/fluent-nova-eventing';
import type { IAddNewAccountEvent } from '@mecontrol/fluent-nova-eventing';
import type {
    AppAcquisitionLinkClickEvent,
    LaunchTileEventOriginator,
    TileUninstallEvent,
    TileLaunchEvent,
    TileLaunchEventData,
    TileMoreInfoEvent,
    FlyoutButtonHoverEvent,
    FlyoutOpenedEvent,
    AppBarImpressionEvent,
    AppBarRenderErrorEvent,
    ContentDroppedEvent,
    TilePermissionsEvent,
} from '@1js/office-start-appbar-events';
import type {
    StartCallViaSipProtocolEvent,
    StartChatViaSipProtocolEvent,
    ImportContactsEvent,
    ExportContactsEvent,
    ComposeEmailEvent,
    OpenLPCEvent,
    OpenLivePersonaEditorEvent,
    OpenPersonSourcesEvent,
    OpenProfileEditorEvent,
    InitializeLPCEvent,
    ShowToastEvent,
    RefreshPersonInfoEvent,
    TrapLpcFocusEvent,
    UntrapLpcFocusEvent,
    StartChatViaImAddressUrlEvent,
    StartCallViaPhoneUrlEvent,
    StartShareContactsAsVCardEvent,
    EditPrivateDistributionListEvent,
} from 'owa-app-host-events';
import { AppHostServiceEventTypes } from 'owa-app-host-events';
import type { MailboxInfo } from 'owa-client-types';
import type { PXNovaTelemetryEvent } from '@1js/midgard-types';
import type {
    CalendarSidePanelEventUnion,
    CalendarSidePanelOwaNovaEventMap,
} from 'calendar-side-panel-event-types';
import { CalendarSidePanelEventTypes } from 'calendar-side-panel-event-types';

/**
 * Extend OWA-specific Nova event types.
 */
// MessageExtensions apps  launch tile event
type TileMessageExtensionLaunchEventData = TileLaunchEventData & {
    launchContext: LinkedEntity;
};
export interface TileMessageExtensionLaunchEvent
    extends NovaEvent<TileMessageExtensionLaunchEventData> {
    originator: typeof LaunchTileEventOriginator;
    type: typeof AppBarEventTypes.launch;
    data?: () => TileMessageExtensionLaunchEventData;
}

// SettingsPage context menu item open.
// Can either hold serialized (from Nova -> owa-nova-actions) or de-serialized data (from owa-nova-actions -> OWA)
type SettingsContextMenuEventData = {
    appId: string;
    mailboxInfo?: MailboxInfo;
};
export interface SettingsContextMenuEvent extends NovaEvent<SettingsContextMenuEventData> {
    originator: 'ContextMenu';
    type: 'settings';
    data?: () => SettingsContextMenuEventData;
}

//
type PinTaskbarShortcutEventData = {
    appId: string;
};
export interface PinTaskbarShortcutEvent extends NovaEvent<PinTaskbarShortcutEventData> {
    originator: 'ContextMenu';
    type: 'pinTaskbarShortcut';
    data?: () => PinTaskbarShortcutEventData;
}

export type PinMessageExtensionEventData = {
    appId: string;
};
export interface PinMessageExtensionEvent extends NovaEvent<PinMessageExtensionEventData> {
    originator: 'ContextMenu';
    type: 'pinMessageExtension';
    data?: () => PinMessageExtensionEventData;
}
export interface PermissionsRequestDialogEvent
    extends NovaEvent<CurrentAppPermissionsRequestDialogEventData> {
    originator: 'CurrentAppPermissionsRequestDialog';
    type: 'cancelPermissionsChange' | 'savePermissionsChange';
    data?: () => CurrentAppPermissionsRequestDialogEventData;
}

/**
 * Union of all Nova's Events.
 */
type NovaEventUnion =
    | AppAcquisitionLinkClickEvent
    | SettingsContextMenuEvent
    | PinTaskbarShortcutEvent
    | TileLaunchEvent
    | TileMoreInfoEvent
    | TileUninstallEvent
    | AppBarRenderErrorEvent
    | AppBarImpressionEvent
    | FlyoutButtonHoverEvent
    | FlyoutOpenedEvent
    | OpenLPCEvent
    | InitializeLPCEvent
    | PinMessageExtensionEvent
    | PXNovaTelemetryEvent
    | LivePersonaEditorInternalEvent
    | OpenLivePersonaEditorEvent
    | PersonSourcesInternalEvent
    | OpenPersonSourcesEvent
    | OpenProfileEditorEvent
    | ComposeEmailEvent
    | ImportContactsEvent
    | ExportContactsEvent
    | StartCallViaSipProtocolEvent
    | StartChatViaSipProtocolEvent
    | StartCallViaPhoneUrlEvent
    | StartChatViaImAddressUrlEvent
    | ShowToastEvent
    | ContentDroppedEvent
    | RefreshPersonInfoEvent
    | TrapLpcFocusEvent
    | UntrapLpcFocusEvent
    | StartShareContactsAsVCardEvent
    | EditPrivateDistributionListEvent
    | CalendarSidePanelEventUnion
    | TilePermissionsEvent
    | PermissionsRequestDialogEvent
    | IAddNewAccountEvent;

/**
 * Maps OWA scenarios to a Nova's Event object type.
 * OWA scenarios tags satchel actions that gets dispatched internally.
 */
type OwaNovaEventMap = CalendarSidePanelOwaNovaEventMap & {
    aboutDialog: TileMoreInfoEvent;
    addIn: TileMessageExtensionLaunchEvent;
    appBar: TileLaunchEvent;
    appStore: AppAcquisitionLinkClickEvent;
    messageExtensions: TileMessageExtensionLaunchEvent;
    settings: SettingsContextMenuEvent;
    uninstall: TileUninstallEvent;
    flyoutButtonHover: FlyoutButtonHoverEvent;
    flyoutOpened: FlyoutOpenedEvent;
    appbarImpression: AppBarImpressionEvent;
    renderError: AppBarRenderErrorEvent;
    pinTaskbarShortcut: PinTaskbarShortcutEvent;
    openLPC: OpenLPCEvent;
    initializeLPC: InitializeLPCEvent;
    pinMessageExtension: PinMessageExtensionEvent;
    openLivePersonaEditor: OpenLivePersonaEditorEvent;
    livePersonaEditorInternal: LivePersonaEditorInternalEvent;
    openPersonSources: OpenPersonSourcesEvent;
    openProfileEditor: OpenProfileEditorEvent;
    personSourcesInternal: PersonSourcesInternalEvent;
    composeEmail: ComposeEmailEvent;
    importContacts: ImportContactsEvent;
    exportContacts: ExportContactsEvent;
    startChatViaImAddressUrl: StartChatViaImAddressUrlEvent;
    startCallViaPhoneUrl: StartCallViaPhoneUrlEvent;
    startCallViaSipProtocol: StartCallViaSipProtocolEvent;
    startChatViaSipProtocol: StartChatViaSipProtocolEvent;
    startShareContactsAsVCard: StartShareContactsAsVCardEvent;
    editPrivateDistributionList: EditPrivateDistributionListEvent;
    showToast: ShowToastEvent;
    contentDropped: ContentDroppedEvent;
    refreshPersonInfo: RefreshPersonInfoEvent;
    trapLpcFocus: TrapLpcFocusEvent;
    untrapLpcFocus: UntrapLpcFocusEvent;
    permissionsDialog: TilePermissionsEvent;
    permissionsDialogSave: PermissionsRequestDialogEvent;
    permissionsDialogClose: PermissionsRequestDialogEvent;
    addNewAccount: IAddNewAccountEvent;
};
type OwaNovaTypes = keyof OwaNovaEventMap;

// Helper that defines a generic OWA Nova Action.
// Tag each action with `owaType` so we can discriminate a single action in a union of actions
// by manually declarating e.g., OwaNovaAction<'appBar'>.
type OwaNovaAction<
    Types extends OwaNovaTypes,
    Events extends OwaNovaEventMap[OwaNovaTypes] = OwaNovaEventMap[OwaNovaTypes]
> = (event: Events) => {
    event: OwaNovaEventMap[Types];
    owaType: Types;
};

// Union all possible OWA Nova Action types.
type OwaNovaActionUnion<
    Types extends OwaNovaTypes = OwaNovaTypes,
    Events extends OwaNovaEventMap[OwaNovaTypes] = OwaNovaEventMap[OwaNovaTypes]
> = {
    [Type in Types]: OwaNovaAction<Type, Events>;
}[Types];

const NovaEventTypes: {
    readonly settings: 'settings';
    readonly pinTaskbarShortcut: 'pinTaskbarShortcut';
    readonly pinMessageExtension: 'pinMessageExtension';
    readonly savePermissions: 'savePermissionsChange';
    readonly closePermissions: 'cancelPermissionsChange';
} & typeof AppBarEventTypes &
    typeof AppHostServiceEventTypes &
    typeof CalendarSidePanelEventTypes &
    typeof MeControlEventTypes = {
    ...AppBarEventTypes,
    ...AppHostServiceEventTypes,
    ...CalendarSidePanelEventTypes,
    ...MeControlEventTypes,
    settings: 'settings',
    pinTaskbarShortcut: 'pinTaskbarShortcut',
    pinMessageExtension: 'pinMessageExtension',
    savePermissions: 'savePermissionsChange',
    closePermissions: 'cancelPermissionsChange',
} as const;

export { type NovaEventUnion, type OwaNovaActionUnion, type OwaNovaAction, NovaEventTypes };
